import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

import { differenceInCalendarDays, format, parseISO } from "date-fns";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function formatDate(paramsDate?: string | null): string {
	if (!paramsDate) return "No due date";

	const date = parseISO(paramsDate);
	const today = new Date();

	const diffDays = differenceInCalendarDays(date, today);

	if (diffDays === 0) return "Today";
	if (diffDays === 1) return "Tomorrow";
	if (diffDays === -1) return "Yesterday";

	if (diffDays > 1 && diffDays <= 14) {
		return `In ${diffDays} days`;
	}

	if (diffDays < -1 && diffDays >= -14) {
		return `${Math.abs(diffDays)} days ago`;
	}

	return format(date, "MMM d, yyyy");
}

/**
 * Takes any valid CSS color string and returns it with the specified opacity in RGBA format.
 *
 * @param color - Any valid CSS color format (Hex, RGB, HSL, Named color)
 * @param opacity - A number between 0 and 1 representing the alpha channel
 * @returns The color string formatted as rgba(r, g, b, a)
 */

function parseHex(hex: string): [number, number, number] | null {
	const h = hex.replace("#", "");
	let r: number, g: number, b: number;

	if (h.length === 3 || h.length === 4) {
		r = parseInt(h[0] + h[0], 16);
		g = parseInt(h[1] + h[1], 16);
		b = parseInt(h[2] + h[2], 16);
	} else if (h.length === 6 || h.length === 8) {
		r = parseInt(h.slice(0, 2), 16);
		g = parseInt(h.slice(2, 4), 16);
		b = parseInt(h.slice(4, 6), 16);
	} else {
		return null;
	}

	if ([r, g, b].some(Number.isNaN)) return null;
	return [r, g, b];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
	s /= 100;
	l /= 100;
	const k = (n: number) => (n + h / 30) % 12;
	const a = s * Math.min(l, 1 - l);
	const f = (n: number) =>
		l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
	return [
		Math.round(255 * f(0)),
		Math.round(255 * f(8)),
		Math.round(255 * f(4)),
	];
}

let sharedCtx: CanvasRenderingContext2D | null | undefined;
const INVALID_SENTINEL = "#010203";

function getSharedCtx(): CanvasRenderingContext2D | null {
	if (sharedCtx !== undefined) return sharedCtx;
	if (typeof document === "undefined") {
		sharedCtx = null;
		return sharedCtx;
	}
	const canvas = document.createElement("canvas");
	canvas.width = 1;
	canvas.height = 1;
	sharedCtx = canvas.getContext("2d", { willReadFrequently: true });
	return sharedCtx;
}

export function colorWithAlpha(color: string, opacity: number = 1): string {
	const safeOpacity = Number.isFinite(opacity)
		? Math.max(0, Math.min(1, opacity))
		: 1;
	const c = color.trim();

	// Hex — parsed directly, works in SSR/Node with no DOM.
	if (c.startsWith("#")) {
		const rgb = parseHex(c);
		if (rgb) return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${safeOpacity})`;
		return color;
	}

	// rgb()/rgba() — parsed directly.
	const rgbMatch = c.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
	if (rgbMatch) {
		const [, r, g, b] = rgbMatch;
		return `rgba(${r}, ${g}, ${b}, ${safeOpacity})`;
	}

	// hsl()/hsla() — parsed directly.
	const hslMatch = c.match(
		/^hsla?\(\s*(-?\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)%\s*,\s*(\d+(?:\.\d+)?)%/i,
	);
	if (hslMatch) {
		const [, h, s, l] = hslMatch;
		const [r, g, b] = hslToRgb(Number(h), Number(s), Number(l));
		return `rgba(${r}, ${g}, ${b}, ${safeOpacity})`;
	}

	// Named colors (or anything else) — only path that needs the DOM.
	const ctx = getSharedCtx();
	if (!ctx) return color;

	ctx.fillStyle = INVALID_SENTINEL;
	ctx.fillStyle = c;
	if (ctx.fillStyle === INVALID_SENTINEL) return color;

	const normalized = ctx.fillStyle as string;
	const match = normalized.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/);
	if (!match) return color;

	const [, r, g, b] = match;
	return `rgba(${r}, ${g}, ${b}, ${safeOpacity})`;
}
